import { useId } from "react";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Separator } from "./ui/separator";
import { MusicPicker } from "./MusicPicker";
import { db } from "../services/Firebase";
import { cn } from "../lib/utils";
import { useToast } from "./ui/use-toast";

export const VIDEO_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5];

interface VideoAudioSettingsProps {
  /** Post to persist changes to; when absent, changes stay local */
  postId?: string;
  musicPath?: string;
  audioMuted: boolean;
  speed: number;
  onMusicChange: (path: string, name: string) => void;
  onMuteChange: (muted: boolean) => void;
  onSpeedChange: (speed: number) => void;
  /** Smaller labels (mobile collapsible) */
  compact?: boolean;
  showSpeed?: boolean;
  disabled?: boolean;
  /** Optional owner-provided persistence queue for coordinating publish/revert. */
  onPersistSettings?: (fields: Record<string, unknown>) => Promise<void>;
  /**
   * Write changes to the post's video fields (default). Set false when the
   * caller owns persistence — e.g. per-connection overrides in a drill-down.
   * postId is still used for music uploads.
   */
  persist?: boolean;
}

/**
 * The video audio/pacing knobs (music, mute, speed) shared by the editor's
 * video settings panel, the mobile video collapsible, and the per-channel
 * rendition preview rail. Persists to the post-level video fields the
 * publish-time render reads (videoMusicPath/videoMusicName/videoAudioMuted/
 * videoSpeed) and reports changes up for the caller's preview state.
 */
export function VideoAudioSettings({
  postId,
  musicPath,
  audioMuted,
  speed,
  onMusicChange,
  onMuteChange,
  onSpeedChange,
  compact = false,
  showSpeed = true,
  disabled = false,
  onPersistSettings,
  persist: persistToPost = true,
}: VideoAudioSettingsProps) {
  const switchId = useId();
  const { toast } = useToast();

  const persist = (fields: Record<string, unknown>) => {
    if (!persistToPost || !postId) return;

    const request = onPersistSettings
      ? onPersistSettings(fields)
      : (async () => {
          const { doc, updateDoc } = await import("firebase/firestore");
          await updateDoc(doc(db, "posts", postId), {
            ...fields,
            updatedAt: Date.now(),
          });
        })();

    void request.catch((error) => {
      console.error("Failed to save video settings:", error);
      toast({
        variant: "destructive",
        title: "Couldn't save video settings",
        description: "Your preview changed, but the setting wasn't saved. Try again.",
      });
    });
  };

  // Order matters: speed is one compact row, the music library is an
  // open-ended scrolling list. The list goes LAST so nothing hides below it
  // — users consistently missed controls placed after the library.
  return (
    <fieldset
      disabled={disabled}
      className={cn(
        "min-w-0 space-y-3 border-0 p-0",
        disabled && "opacity-60"
      )}
    >
      {showSpeed && (
        <>
          <div className="space-y-2">
            <Label className={cn(compact && "text-xs")}>Animation Speed</Label>
            <div className="flex gap-1">
              {VIDEO_SPEEDS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    onSpeedChange(s);
                    persist({ videoSpeed: s });
                  }}
                  className={cn(
                    "flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors",
                    compact && "min-h-11",
                    speed === s
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  )}
                >
                  {s}x
                </button>
              ))}
            </div>
          </div>
          <Separator />
        </>
      )}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor={switchId} className={cn(compact && "text-xs")}>
            Music
          </Label>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              {audioMuted ? "Off" : "On"}
            </span>
            <Switch
              id={switchId}
              checked={!audioMuted}
              aria-label={audioMuted ? "Turn music on" : "Turn music off"}
              onCheckedChange={(checked) => {
                const muted = !checked;
                onMuteChange(muted);
                persist({ videoAudioMuted: muted });
              }}
            />
          </div>
        </div>
        {!audioMuted && (
          <MusicPicker
            selectedMusicPath={musicPath}
            postId={postId}
            disabled={disabled}
            onSelectTrack={(path, name) => {
              onMusicChange(path, name);
              persist({ videoMusicPath: path, videoMusicName: name });
            }}
          />
        )}
      </div>
    </fieldset>
  );
}
